import { Fragment, type ReactNode, useState } from "react";
import {
  Box,
  type BoxProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from "@hightouch/htui";

export type MenuOption = {
  /** Stable identity for React keys; falls back to array index when omitted. */
  id?: string;
  label: string;
  icon?: React.ComponentType;
  variant?: "default" | "danger";
  onClick: () => void;
  isDisabled?: boolean;
  dividerBefore?: boolean;
};

/**
 * Renders MenuOptions into MenuItems. Shared by the hover overflow menu and the
 * right-click context menu so the two renderings can't drift (keys, variant
 * narrowing, click propagation).
 */
export function renderMenuItems(options: MenuOption[]): ReactNode {
  return options.map((option, index) => (
    <Fragment key={option.id ?? index}>
      {option.dividerBefore && index > 0 && <MenuDivider />}
      <MenuItem
        icon={option.icon}
        variant={option.variant === "danger" ? "danger" : undefined}
        isDisabled={option.isDisabled}
        onClick={(e) => {
          e.stopPropagation();
          option.onClick();
        }}
      >
        {option.label}
      </MenuItem>
    </Fragment>
  ));
}

type CursorPosition = { x: number; y: number };

export function useCursorContextMenu(enabled = true) {
  const [position, setPosition] = useState<CursorPosition | null>(null);

  const onContextMenu = enabled
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPosition({ x: e.clientX, y: e.clientY });
      }
    : undefined;

  return {
    contextMenuTriggerProps: { onContextMenu },
    contextMenuProps: { position, onClose: () => setPosition(null) },
    isContextMenuOpen: position !== null,
    /** Opens the menu at a point, for a keyboard shortcut that has no cursor to place it at. */
    openContextMenuAt: (point: CursorPosition) => {
      if (enabled) setPosition(point);
    },
  };
}

type CursorContextMenuProps = {
  position: CursorPosition | null;
  zIndex?: BoxProps["zIndex"];
  children: ReactNode;
  onClose: () => void;
};

export function CursorContextMenu({
  position,
  zIndex = 2,
  children,
  onClose,
}: CursorContextMenuProps) {
  if (!position) {
    return null;
  }

  return (
    <Box
      position="fixed"
      left={`${position.x}px`}
      top={`${position.y}px`}
      zIndex={zIndex}
      css={{
        "& button": {
          width: 0,
          height: 0,
          minWidth: 0,
          minHeight: 0,
          padding: 0,
          border: "none",
          opacity: 0,
          pointerEvents: "none",
        },
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Menu isLazy isOpen onClose={onClose}>
        <MenuButton aria-hidden tabIndex={-1}>
          {""}
        </MenuButton>
        <MenuList>{children}</MenuList>
      </Menu>
    </Box>
  );
}
